import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useCallback } from 'react';

import styles from './SortControl.module.scss';

type Sortable = Record<string, any>;

interface SortControlProps {
  index: number;
  sort: number;
  sortKey: keyof Sortable;
  sortable: Sortable[];
  onSortChange: (sortable: Sortable[]) => void;
  showArrows?: boolean;
}

const validateOrder = (value: string, maxOrder: number) => {
  value = value.replace(/\D/g, '');
  const order = Number(value);

  if (order > maxOrder || order < 1) {
    return;
  }

  return order;
};

export const SortControl = ({
  index,
  sort,
  sortKey,
  sortable,
  onSortChange,
  showArrows = true,
}: SortControlProps) => {
  const reorder = useCallback(
    (items: Sortable[], startIndex: number, endIndex: number) => {
      const result = Array.from(items);
      const [removed] = result.splice(startIndex, 1);

      result.splice(endIndex, 0, removed);

      const reordered = result.map((item, i) => ({
        ...item,
        [sortKey]: i + 1,
      }));

      return reordered;
    },
    [sortKey],
  );

  const handleMove = useCallback(
    (startIndex: number, endIndex: number) => {
      const items = reorder(sortable, startIndex, endIndex);

      onSortChange(items);
    },
    [onSortChange, reorder, sortable],
  );

  const handleOrder = useCallback(
    (currentIndex: number, value: string) => {
      const newIndex = validateOrder(value, sortable.length);

      if (!newIndex) return;

      // Subtract 1 to account for 0-based index
      const items = reorder(sortable, currentIndex, newIndex - 1);

      onSortChange(items);
    },
    [onSortChange, reorder, sortable],
  );

  return (
    <div className={styles.component} data-ui="sort-control">
      {showArrows && (
        <button
          disabled={index === 0}
          onClick={() => handleMove(index, index - 1)}
          type="button"
        >
          <KeyboardArrowUp />
          <span className="sr-only">Move up</span>
        </button>
      )}
      <label>
        <span className="sr-only">Sort order</span>
        <select
          name="sort"
          onChange={(e) => handleOrder(index, e.target.value)}
          value={sort}
        >
          {Array.from({ length: sortable.length }).map((_, i) => (
            <option key={i} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </select>
      </label>
      {showArrows && (
        <button
          disabled={index === sortable.length - 1}
          onClick={() => handleMove(index, index + 1)}
          type="button"
        >
          <KeyboardArrowDown />
          <span className="sr-only">Move down</span>
        </button>
      )}
    </div>
  );
};
